/** 
 Copyright Highway9 Networks Inc. 
 */

/**
 * The `geoDistance` function calculates the distance between two geographical coordinates using the
 * Haversine formula in TypeScript.
 * @param {number} lat1 - Latitude of the first point in degrees
 * @param {number} lon1 - Longitude of the first location in degrees
 * @param {number} lat2 - Latitude of the second point
 * @param {number} lon2 - Longitude of the second point
 * @returns The `geoDistance` function returns the distance in meters between two sets of geographical
 * coordinates (latitude and longitude) provided as input parameters.
 * 
 * @example
 * const distance = geoDistance(51.5074, -0.1278, 40.7128, -74.0060);
 * console.log(distance); // 5570480 (5.57 km)
 */
export const geoDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // Radius of the earth in m

  // distance between latitudes and longitudes
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in m
};

// Path: src\helpers\geoDistance.ts
// convert degree to radian
export const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};


const directions = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
];

/**
 * Formats a compass bearing (in degrees) into its nearest 16-point compass
 * direction. Shared by {@link calculateDirection} and {@link reverseDirection},
 * and reusable wherever a precomputed bearing (e.g. backend-provided PAL
 * proximity `direction`) needs a human-readable label.
 *
 * @param {number} bearing - Bearing in degrees (any range; normalized to 0-360).
 * @returns {{ direction: string, bearing: number, value: string }} The compass
 *   direction, the normalized bearing, and a formatted value (e.g. "NE (45°)").
 *
 * @example
 * formatBearing(137); // { direction: 'SE', bearing: 137, value: 'SE (137°)' }
 */
export const formatBearing = (
  bearing: number
): { direction: string; bearing: number; value: string } => {
  if (typeof bearing !== 'number' || Number.isNaN(bearing)) {
    return { direction: 'N/A', bearing: 0, value: 'N/A' };
  }

  // Normalize to [0, 360)
  const normalized = ((bearing % 360) + 360) % 360;

  // There are 16 compass directions, each covering 22.5 degrees
  const index = Math.round(normalized / 22.5) % 16;

  return {
    direction: directions[index],
    bearing: normalized,
    value: `${directions[index]} (${normalized.toFixed(0)}°)`,
  };
};

/**
 * Formats a distance in meters into a compact human-readable string, switching
 * to kilometres for distances of 1 km or more.
 *
 * @param {number} meters - Distance in meters.
 * @returns {string} Formatted distance (e.g. "350 m" or "1.25 km").
 *
 * @example
 * formatDistance(350);  // "350 m"
 * formatDistance(1250); // "1.25 km"
 */
export const formatDistance = (meters: number): string => {
  if (typeof meters !== 'number' || Number.isNaN(meters)) return 'N/A';
  return meters >= 1000 ? `${(meters / 1000).toFixed(2)} km` : `${meters.toFixed(0)} m`;
};

/**
 * Calculates the compass direction (bearing) from one geographic coordinate to another.
 * Returns an object with the compass direction, the bearing in degrees, and a formatted value.
 *
 * @param {number} lat1 - Latitude of the starting point in degrees.
 * @param {number} lon1 - Longitude of the starting point in degrees.
 * @param {number} lat2 - Latitude of the destination point in degrees.
 * @param {number} lon2 - Longitude of the destination point in degrees.
 * @returns {{ direction: string, bearing: number, value: string }} - The compass direction and bearing in degrees (e.g., "NE (45°)").
 *
 * @example
 * // Calculate the direction from San Francisco to Los Angeles
 * const dir = calculateDirection(37.7749, -122.4194, 34.0522, -118.2437);
 * console.log(dir); // Output: e.g., { direction: 'SE', bearing: 137, value: 'SE (137°)' }
 */
export const calculateDirection = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): { direction: string, bearing: number, value: string } => {
  // Validate input
  if (
    typeof lat1 !== 'number' ||
    typeof lon1 !== 'number' ||
    typeof lat2 !== 'number' ||
    typeof lon2 !== 'number'
  ) {
    return { direction: 'N/A', bearing: 0, value: 'N/A' };
  }

  // Convert degrees to radians
  const toRad = (deg: number) => deg * (Math.PI / 180);

  const dLon = toRad(lon2 - lon1);
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);

  // Calculate bearing using the formula for initial bearing between two points
  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  const bearing = Math.atan2(y, x) * (180 / Math.PI);

  return formatBearing(bearing);
};


/**
 * Reverses the direction of a compass bearing.
 *
 * @param {number} angle - The original compass bearing in degrees.
 * @returns { { direction: string, bearing: number, value: string } } The reversed compass direction and bearing in degrees. 
 *
 * @example
 * const reversed = reverseDirection(45);
 * console.log(reversed); // Output: { direction: 'SW', bearing: 225, value: 'SW (225°)' }
 */
export const reverseDirection = (angle: number): { direction: string, bearing: number, value: string } => {
  return formatBearing(angle + 180);
}
